/* Rocos order/session linkage. Uses the existing Google tag; emits no Google events. */
(() => {
  'use strict';
  if (window.__rocosMetricsContext || window.Shopify?.designMode) return;
  const status = window.__rocosMetricsContext = {version: '1.0.1', mode: 'observe', state: 'waiting_consent'};
  const endpoint = 'https://metrics.rocos.ro/tracking/context';
  const storageKey = 'rocos_metrics_consent_context_v1';
  const attribute = 'rocos_metrics__';
  let memory = null, busy = false, queued = false, lastFingerprint = '', lastCapture = 0;
  let retries = 0, timer = null, crossTabRevoked = false;
  function saved() {
    if (memory) return memory;
    try { memory = JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch { /* storage can be unavailable */ }
    return memory;
  }
  function persist(value) {
    memory = value;
    try { if (value) localStorage.setItem(storageKey, JSON.stringify(value)); else localStorage.removeItem(storageKey); } catch { /* best effort */ }
  }
  function consent() {
    const p = window.Shopify?.customerPrivacy;
    return {analytics: p?.analyticsProcessingAllowed?.() === true,
      marketing: p?.marketingAllowed?.() === true, sale_of_data: p?.saleOfDataAllowed?.() === true};
  }
  function get(field) {
    return new Promise(resolve => {
      if (typeof window.gtag !== 'function') return resolve(null);
      const timeout = setTimeout(() => resolve(null), 1500);
      window.gtag('get', 'G-1DL4Z4YJEP', field, value => { clearTimeout(timeout); resolve(value); });
    });
  }
  async function post(body) {
    const response = await fetch(endpoint, {method: 'POST', credentials: 'omit', mode: 'cors',
      headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body),
      keepalive: true, signal: AbortSignal.timeout(5000)});
    if (!response.ok) { const error = new Error('collector_unavailable'); error.status = response.status; throw error; }
    return response.json();
  }
  async function cart(token) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + 'cart/update.js', {method: 'POST', credentials: 'same-origin',
      headers: {'Content-Type': 'application/json'}, body: JSON.stringify({attributes: {[attribute]: token}}),
      keepalive: true, signal: AbortSignal.timeout(5000)});
    if (!response.ok) throw new Error('cart_link_unavailable');
  }
  async function revoke() {
    const old = saved();
    if (!old?.capability) { status.state = 'consent_denied'; return; }
    persist({...old, pendingRevoke: true});
    // Removing an existing linkage is necessary cleanup after consent withdrawal.
    await post({capability: old.capability, revoke: true}).catch(e => { if (e.status !== 410) throw e; });
    await cart('');
    persist(null); lastFingerprint = ''; status.state = 'revoked';
  }
  async function capture() {
    if (crossTabRevoked) { status.state = 'consent_denied'; return; }
    const initial = consent();
    if (!initial.analytics || saved()?.pendingRevoke) { await revoke(); if (!initial.analytics) return; }
    // The app configures multiple Google destinations after consent. Reading an
    // ID before its own first page_view can return an identity from initialization.
    const ready = (window.dataLayer || []).some(entry => entry?.[0] === 'event' && entry[1] === 'page_view' &&
      [entry[2]?.send_to].flat().some(target => String(target).split(',').includes('G-1DL4Z4YJEP')));
    if (!ready) { status.state = 'waiting_google_page_view'; return; }
    const [clientId, sessionId] = await Promise.all([get('client_id'), get('session_id')]);
    if (crossTabRevoked || !consent().analytics) { await revoke(); return; }
    if (!/^\d{1,20}\.\d{1,20}$/.test(String(clientId)) || !/^\d{10}$/.test(String(sessionId))) {
      status.state = 'waiting_identifiers'; return;
    }
    const current = consent();
    const clickIds = {};
    if (current.marketing) {
      const params = new URLSearchParams(location.search);
      for (const name of ['gclid','gbraid','wbraid']) {
        const value = params.get(name);
        if (value && /^[A-Za-z0-9_.~-]{1,512}$/.test(value)) clickIds[name] = value;
      }
    }
    const fingerprint = JSON.stringify([clientId,sessionId,current,clickIds]);
    if (lastFingerprint === fingerprint && Date.now() - lastCapture < 60000) return;
    const body = {client_id: String(clientId), session_id: String(sessionId), consent: current,
      click_ids: clickIds, capability: saved()?.capability || ''};
    let result;
    try { result = await post(body); }
    catch (error) {
      if (error.status === 410) { persist(null); lastFingerprint = ''; }
      throw error;
    }
    persist({capability: result.capability, pendingRevoke: false});
    if (crossTabRevoked || !consent().analytics) { await revoke(); return; }
    // Cart writes are serialized, touch one private attribute, and never delay checkout navigation.
    await cart(result.cart_token);
    lastFingerprint = fingerprint; lastCapture = Date.now(); retries = 0;
    status.state = 'linked';
  }
  async function run() {
    if (busy) { queued = true; return; }
    busy = true;
    try { await capture(); }
    catch { status.state = 'retry_pending'; }
    finally {
      busy = false;
      clearTimeout(timer);
      if (queued) { queued = false; timer = setTimeout(run, 50); }
      else if ((!crossTabRevoked && consent().analytics) || saved()?.pendingRevoke) timer = setTimeout(run, retries++ < 8 ? 2500 : 60000);
    }
  }
  document.addEventListener('visitorConsentCollected', () => { crossTabRevoked = false; lastFingerprint = ''; run(); });
  window.addEventListener('pageshow', () => run());
  window.addEventListener('storage', event => {
    if (event.key !== storageKey) return;
    memory = null; lastFingerprint = '';
    // Another tab may withdraw consent before this tab's privacy API updates.
    if (event.oldValue && !event.newValue) crossTabRevoked = true;
    run();
  });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') run(); });
  if (window.Shopify?.customerPrivacy) run();
  else if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => {
    if (!error) run(); else status.state = 'privacy_api_unavailable';
  });
})();
